import { ANIMATIONS, animationFor } from './animations';
import { SPRITE_SIZE } from './events';

/**
 * A sprite sheet, and the ability to draw one frame of it.
 *
 * DELIBERATELY STATELESS with respect to entities. One instance serves every
 * player; which frame to draw is derived from a clock plus the entity's own
 * state, rather than stored per entity. That keeps animation from becoming
 * another thing to synchronise, and means adding the 40th player costs nothing
 * but a draw call.
 *
 * What differs per player is the `source` handed to draw() — a recoloured
 * canvas from SpriteVariants. The animation logic does not care which image it
 * blits from, which is why per-player characters cost this file one parameter.
 *
 * Loading is fire-and-forget: `isLoaded` stays false until the image arrives,
 * and the caller is expected to fall back to something simple until then. A
 * missing or broken sheet must never take the game down with it.
 */
export default class Sprite {
  constructor(src, { frameSize = SPRITE_SIZE } = {}) {
    this.frameSize = frameSize;
    this.isLoaded = false;
    this.failed = false;

    /**
     * What `drawFrame` blits from.
     *
     * Normally the loaded Image, but a recoloured canvas can be swapped in —
     * `drawImage` accepts either interchangeably. That is how one sheet becomes
     * every player's individual character without loading anything extra.
     */
    this.source = null;

    this.image = new Image();
    this.image.onload = () => {
      this.isLoaded = true;
      this.source = this.image;
    };
    this.image.onerror = () => {
      this.failed = true;
      // Surfaced rather than swallowed: a silently missing sheet looks like a
      // rendering bug, and this is the one line that explains it.
      // eslint-disable-next-line no-console
      console.warn(`[Sprite] failed to load "${src}" — falling back to shapes`);
    };
    this.image.src = src;
  }

  /**
   * Draw one cell of the sheet, centred on (x, y).
   *
   * Uses the 9-argument drawImage: source crop rect, then destination rect.
   * Coordinates are floored because drawing a sprite on a half-pixel makes the
   * browser interpolate it, which undoes `image-rendering: pixelated` and
   * turns crisp pixel art to mush.
   */
  drawFrame(ctx, col, row, x, y, source = null) {
    const from = source || this.source;
    if (!from) return;

    const size = this.frameSize;
    const half = size / 2;

    ctx.drawImage(
      from,
      col * size,
      row * size,
      size,
      size,
      Math.floor(x - half),
      Math.floor(y - half),
      size,
      size,
    );
  }

  /**
   * Draw the correct frame for an entity's authoritative state.
   *
   * @param {object} opts
   * @param {number} opts.nowMs   - shared clock, from performance.now()
   * @param {number} opts.phase   - per-entity offset in ms, so players do not
   *                                march in lockstep
   */
  draw(ctx, {
    x, y, facing, moving, nowMs, phase = 0, source = null,
  }) {
    // `source` is a per-player recoloured canvas. Fall through to the base
    // sheet when there is none — a player whose appearance has not arrived
    // should still be drawn, in placeholder colours, rather than vanish.
    if (!source && !this.isLoaded) return false;

    const animation = ANIMATIONS[animationFor(facing, moving)];
    if (!animation) return false;

    const { frames, frameMs } = animation;
    const index = Number.isFinite(frameMs)
      ? Math.floor((nowMs + phase) / frameMs) % frames.length
      : 0;

    const [col, row] = frames[index];
    this.drawFrame(ctx, col, row, x, y, source);
    return true;
  }
}
